/**
 * Hooks para Márgenes - REPLICAR ESTRUCTURA EXACTA DE useAnalytics.ts
 */

import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useEffect, useMemo, useState, useRef } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import MarginsService, {
  MarginsLevel1Metrics,
} from "../services/margins.service";
import { shiftMonthKeyYear } from "../components/Margenes/margenes-format";

/**
 * Hook para métricas principales de márgenes (Level 1).
 * Bloque 10 plan dashboard_ux_ventas_margenes 2026-05-21: acepta opts opcional
 * con startMonth/endMonth/storeName. queryKey incluye opts para cache distinct.
 */
export const useMarginsLevel1Metrics = (
  enabled: boolean = true,
  opts?: {
    startMonth?: string | null;
    endMonth?: string | null;
    storeName?: string | null;
  },
): UseQueryResult<MarginsLevel1Metrics> => {
  const [authReady, setAuthReady] = useState(false);
  const retryCount = useRef(0);

  useEffect(() => {
    let active = true;
    const checkSession = async () => {
      try {
        const session = await fetchAuthSession();
        if (!active) return;
        if (session.tokens?.idToken || session.tokens?.accessToken) {
          setAuthReady(true);
        } else if (retryCount.current < 10) {
          // hasta ~5s (10 * 500ms)
          retryCount.current += 1;
          setTimeout(checkSession, 500);
        }
      } catch {
        if (retryCount.current < 10) {
          retryCount.current += 1;
          setTimeout(checkSession, 500);
        }
      }
    };
    checkSession();
    return () => {
      active = false;
    };
  }, []);

  const startMonth = opts?.startMonth ?? null;
  const endMonth = opts?.endMonth ?? null;
  const storeName = opts?.storeName ?? null;

  return useQuery({
    queryKey: [
      "margins",
      "level1",
      "monthly",
      startMonth,
      endMonth,
      storeName,
    ],
    queryFn: () =>
      MarginsService.getMarginsLevel1Metrics({
        startMonth,
        endMonth,
        storeName,
      }),
    enabled: enabled && authReady,
    staleTime: 3 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
    retry: 2,
  });
};

/**
 * Alias para DashboardMargenes - mismo hook que useMarginsLevel1Metrics
 */
export const useDashboardMargenesMetrics = useMarginsLevel1Metrics;

/**
 * Margen % por tienda del MISMO período del año anterior, mapeado por storeName.
 * Lo consume el ranking de /margenes para calcular la variación del margen (chip
 * delta) vs año anterior. Reusa el endpoint nivel-1 con el rango desplazado −1 año
 * (React Query cachea por queryKey; el año anterior es estable). Devuelve Map vacío
 * mientras carga → el delta de cada tienda queda null y su chip se oculta.
 */
export const useStoreMarginGrowth = (
  enabled: boolean,
  startMonth: string | null,
  endMonth: string | null,
  storeName: string | null,
): Map<string, number> => {
  const prevQuery = useMarginsLevel1Metrics(enabled, {
    startMonth: shiftMonthKeyYear(startMonth, -1),
    endMonth: shiftMonthKeyYear(endMonth, -1),
    storeName,
  });
  return useMemo(() => {
    const map = new Map<string, number>();
    for (const s of prevQuery.data?.topStores ?? []) {
      map.set(s.storeName, s.avgMarginPercentage);
    }
    return map;
  }, [prevQuery.data]);
};

/**
 * Hook para obtener datos específicos de un mes - REPLICAR useMonthlyData
 */
export const useMonthlyMarginData = (
  year: number,
  month: number,
  enabled: boolean = true,
): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["margins", "monthly", year, month],
    queryFn: () => MarginsService.getMonthlyMarginData(year, month),
    enabled: enabled && !!year && !!month,
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchInterval: 15 * 60 * 1000, // Refetch cada 15 minutos
    retry: 2,
  });
};

